import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';

import {
  Modal,
  ModalContent,
  ModalOverlay,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  Text,
  Heading,
  FormErrorMessage,
  useToast,
  Container,
  Box,
  InputGroup,
  InputLeftAddon,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import { useUIStore } from '~/store/useUIStore';
import { useTranslations } from 'next-intl';
import { ROUTES, EVENTS } from '@habity/constants';
import { useAgency } from '~/hooks/useAgency';
import { useUser } from '~/hooks/useUser';
import { useUsers } from '~/hooks/useUsers';
import { useTracking } from '~/hooks/useTracking';

type UserData = {
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  user_metadata?: {
    full_name?: string;
    name?: string;
    phone?: string;
  };
  terms_accepted_at?: string | null;
};

const extractFullName = (
  userData: UserData | null,
  userMetadata: UserData['user_metadata']
) => {
  if (userData?.name) return userData.name;
  if (userData?.first_name && userData?.last_name)
    return `${userData.first_name} ${userData.last_name}`;
  if (userData?.first_name) return userData.first_name;
  if (userMetadata?.full_name) return userMetadata.full_name;
  if (userMetadata?.name) return userMetadata.name;
  return '';
};

export const ProfileCompletionModal = () => {
  const modals = useUIStore((state) => state.modals);
  const setModals = useUIStore((state) => state.setModals);
  const toast = useToast();
  const i18n = useTranslations('ProfileCompletion');
  const { getLogoSrc, theme } = useAgency();
  const { user, isUserLoaded } = useUser() as TODO;
  const userIsLoading = !isUserLoaded;
  const { updateUser } = useUsers();
  const { track } = useTracking();

  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    acceptTerms: false,
  });

  const [imageData, setImageData] = useState<{
    extension: string;
    url: string;
  } | null>(null);

  const [errors, setErrors] = useState({
    name: '',
    phoneNumber: '',
    acceptTerms: '',
    image: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      const prefillData = {
        name: '',
        phoneNumber: '',
        acceptTerms: false,
      };

      prefillData.name = extractFullName(user, user.user_metadata);

      let phone = user.phone || user.user_metadata?.phone || '';

      if (phone.startsWith('+46')) {
        phone = phone.substring(3);
      }

      if (phone.startsWith('0')) {
        phone = phone.substring(1);
      }

      prefillData.phoneNumber = phone;
      prefillData.acceptTerms = !!user.terms_accepted_at;

      setFormData(prefillData);
    }
  }, [user]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;

    if (name === 'phoneNumber') {
      const numbersOnly = value.replace(/[^\d]/g, '');
      setFormData({
        ...formData,
        [name]: numbersOnly,
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === 'acceptTerms' ? checked : value,
      });
    }

    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleImageUploaded = (uploadedImageData: {
    extension: string;
    url: string;
  }) => {
    setImageData(uploadedImageData);
    if (errors.image) {
      setErrors({ ...errors, image: '' });
    }
  };

  const handleImageRemoved = () => {
    setImageData(null);
  };

  const validate = () => {
    const newErrors = {
      name: '',
      phoneNumber: '',
      acceptTerms: '',
      image: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = i18n('errors.nameRequired');
    } else {
      // Check if both first and last names are provided
      const nameParts = formData.name.trim().split(' ');
      if (nameParts.length < 2 || nameParts.some((part) => !part.trim())) {
        newErrors.name = 'Både för- och efternamn krävs';
      }
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = i18n('errors.phoneRequired');
    } else if (!/^\d{7,10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = i18n('errors.phoneInvalid');
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = i18n('errors.termsRequired');
    }

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (!user) throw new Error('No authenticated user found');

      let firstName = formData.name;
      let lastName = '';

      const nameParts = formData.name.trim().split(' ');
      if (nameParts.length > 1) {
        firstName = nameParts.slice(0, -1).join(' ');
        lastName = nameParts[nameParts.length - 1];
      }

      const formattedPhone = `+46${
        formData.phoneNumber.startsWith('0')
          ? formData.phoneNumber.substring(1)
          : formData.phoneNumber
      }`;

      const termsAcceptedAt = formData.acceptTerms
        ? new Date().toISOString()
        : null;

      const { error: userError } = await updateUser({
        name: formData.name,
        first_name: firstName,
        last_name: lastName,
        phone: formattedPhone,
        terms_accepted_at: termsAcceptedAt,
      });

      if (userError) {
        throw userError;
      }

      // Track user profile updated event
      track(EVENTS.COMPLETED_USER_PROFILE, {
        userId: user.id,
        name: formData.name,
        first_name: firstName,
        last_name: lastName,
        phone: formattedPhone,
        terms_accepted_at: termsAcceptedAt,
      });

      closeModal();

      toast({
        title: i18n('toast.success.title'),
        description: i18n('toast.success.description'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating profile:', error);

      toast({
        title: i18n('toast.error.title'),
        description: i18n('toast.error.description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setModals({ ...modals, profileCompletionOpen: false });
  };

  const onClose = () => {
    if (isSubmitting) {
      return;
    }

    closeModal();
  };

  return (
    <Modal
      isOpen={modals.profileCompletionOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
    >
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent
        my={{ mobile: '0', desktop: '4' }}
        mx="4"
        boxShadow="none"
        overflow="hidden"
      >
        <Container
          maxW="md"
          py={{ base: '8', sm: '8' }}
          px={{ base: '10', sm: '10' }}
          bg={{ base: 'bg-surface', sm: 'bg-surface' }}
          boxShadow={{ base: 'xl', sm: 'xl' }}
          borderRadius={{ base: 'xl', sm: 'xl' }}
        >
          <Stack spacing="8">
            <Stack spacing="6" align="center">
              <Box>
                <img
                  src={getLogoSrc()}
                  alt="Logo"
                  style={{ maxWidth: theme?.logo_max_width || '180px' }}
                />
              </Box>

              <Stack spacing="3" textAlign="center">
                <Heading size="lg">{i18n('title')}</Heading>
                <Text color="muted">{i18n('description')}</Text>
              </Stack>
            </Stack>

            <Stack spacing="2">
              <form onSubmit={handleSubmit}>
                <Stack spacing="4">
                  <FormControl isInvalid={!!errors.name}>
                    <FormLabel>{i18n('form.name.label')}</FormLabel>
                    <Input
                      name="name"
                      placeholder={i18n('form.name.placeholder')}
                      value={formData.name}
                      onChange={handleChange}
                      isDisabled={userIsLoading}
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.phoneNumber}>
                    <FormLabel>{i18n('form.phone.label')}</FormLabel>
                    <InputGroup>
                      <InputLeftAddon
                        children="+46"
                        bgColor="gray.100"
                        color="gray.700"
                        fontWeight="medium"
                        borderColor="gray.300"
                      />
                      <Input
                        name="phoneNumber"
                        placeholder={i18n('form.phone.placeholder')}
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        type="tel"
                        isDisabled={userIsLoading}
                      />
                    </InputGroup>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {i18n('form.phone.hint') ||
                        'Enter without the leading 0, e.g. "712345678"'}
                    </Text>
                    <FormErrorMessage>{errors.phoneNumber}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.acceptTerms}>
                    <Stack direction="row" align="flex-start">
                      <Checkbox
                        name="acceptTerms"
                        isChecked={formData.acceptTerms}
                        onChange={handleChange}
                        colorScheme="brand"
                        isDisabled={userIsLoading}
                      />
                      <Text fontSize="sm">
                        {i18n('form.terms.text')}{' '}
                        <Link
                          href={ROUTES.CMS.TERMS}
                          target="_blank"
                          color="brand.500"
                        >
                          {i18n('form.terms.link')}
                        </Link>
                      </Text>
                    </Stack>
                    <FormErrorMessage>{errors.acceptTerms}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="brand"
                    width="full"
                    isLoading={isSubmitting || userIsLoading}
                    mt={2}
                    bg="#9F5322"
                    _hover={{ bg: '#8A461D' }}
                  >
                    {i18n('form.submitButton')}
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Stack>
        </Container>
      </ModalContent>
    </Modal>
  );
};
